import { useDisclosure } from '@mantine/hooks';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer, TImporterState } from '@feathr/blackbox';
import { ActionMenuCell, ArchiveModal, createActionMenuColumn, toast } from '@feathr/components';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import { getImportType } from './DataImportsPage.utils';
import { hasRunDetails } from './ImportRunDetails/ImportRunDetails.utils';
import type { IImportMenuItem } from './optionsColumn.utils';
import { getImportMenuItems, getRemoveAction, getViewContactsTooltip } from './optionsColumn.utils';

interface IRow {
  original: Importer;
  /** Build the menu from the per-state matrix, beside the expandable rows (`isImportsRevampEnabled`). */
  showRunDetails?: boolean;
}

interface IImporterStateFlags {
  archivable: boolean;
  hasErrors: boolean;
  isComplete: boolean;
  isDraft: boolean;
}

const getImporterState = (
  state: TImporterState | undefined,
  errorsKey: string | undefined,
): IImporterStateFlags => ({
  isComplete: state === 'complete',
  isDraft: state === 'draft',
  archivable: !!state && ['complete', 'failed'].includes(state),
  hasErrors: Boolean(errorsKey),
});

const getModalProps = (type: 'delete' | 'archive', t: TFunction, name: string) => {
  if (type === 'delete') {
    return {
      alertDescription: t(
        'Deleting this import will remove it from the list of imports. This action cannot be undone.',
      ),
      alertTitle: t('Are you sure you want to delete this import?'),
      confirmationPhrase: t('DELETE'),
      confirmButtonText: t('Delete'),
      title: t('Delete "{{- name}}"', { name }),
    };
  }
  return {
    alertDescription: t(
      'Archiving this import will remove it from the list of imports and cannot be undone. Persons created or updated by this import will not be affected.',
    ),
    alertTitle: t('Are you sure you want to archive this import?'),
    confirmationPhrase: t('ARCHIVE'),
    confirmButtonText: t('Archive'),
    title: t('Archive "{{- name}}"', { name }),
  };
};

export const OptionsCell = observer(({ original, showRunDetails = false }: IRow) => {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const { Importers } = useStore();

  const [showArchiveModal, { open: openModal, close: closeModal }] = useDisclosure(false);

  const state = original.get('state');
  const segment = original.get('segment');

  const { isComplete, isDraft, archivable, hasErrors } = getImporterState(
    state,
    original.get('errors_key'),
  );

  /*
   * An import only has contacts to show once it has finished and produced a group;
   * a completed import that matched nobody has neither.
   */
  const canViewContacts = isComplete && Boolean(segment);
  // A partner import adds partners, not contacts, so it never has a group to view.
  const isPartner = original.get('kind') === 'partner';

  const modalType = getRemoveAction(state);
  const modalProps = getModalProps(modalType, t, original.name);

  async function handleAction(action): Promise<void> {
    try {
      await action();

      if (modalType === 'delete') {
        Importers.remove(original.id);
      }

      const successMessage =
        modalType === 'delete'
          ? t('Import deleted successfully.')
          : t('Import archived successfully.');
      toast(successMessage, { type: 'success' });
    } catch (error) {
      const errorMessage =
        modalType === 'delete'
          ? t(`Something went wrong while trying to delete this import: {{- error}}`, { error })
          : t(`Something went wrong while trying to archive this import: {{- error}}`, { error });
      toast(errorMessage, { type: 'error' });
    } finally {
      closeModal();
    }
  }

  function renderMatrixItem({ id, disabledReason }: IImportMenuItem): ReactNode {
    const disabled = Boolean(disabledReason);
    const common = {
      disabled,
      name: `import-${id}`,
      tooltip: disabledReason,
      tooltipPosition: 'left' as const,
    };

    // A disabled item carries no link, so it can't navigate or download from the keyboard.
    switch (id) {
      case 'view-contacts':
        return (
          <ActionMenuCell.Item
            key={id}
            {...common}
            link={disabled ? undefined : localUrl(`/data/segments/${segment}/people`)}
            type={'link'}
          >
            {t('View Contacts')}
          </ActionMenuCell.Item>
        );

      case 'download-file':
        return (
          <ActionMenuCell.Item
            key={id}
            {...common}
            href={disabled ? undefined : original.downloadUrl}
            type={'link'}
          >
            {t('Download File')}
          </ActionMenuCell.Item>
        );

      case 'download-errors':
        return (
          <ActionMenuCell.Item key={id} {...common} href={original.downloadErrorUrl} type={'link'}>
            {t('Download Errors')}
          </ActionMenuCell.Item>
        );

      // 'delete' or 'archive'
      default:
        return (
          <ActionMenuCell.Item
            key={id}
            {...common}
            onClick={disabled ? undefined : openModal}
            prefix={getIconForAction(id)}
            theme={'danger'}
          >
            {id === 'delete' ? t('Delete') : t('Archive')}
          </ActionMenuCell.Item>
        );
    }
  }

  const items = showRunDetails
    ? getImportMenuItems(
        {
          canExpand: hasRunDetails(original),
          hasErrors,
          hasGroup: Boolean(segment),
          kind: getImportType(original),
          state,
        },
        t,
      ).map(renderMatrixItem)
    : [
        !isPartner && (
          <ActionMenuCell.Item
            disabled={!canViewContacts}
            key={'view-contacts'}
            link={canViewContacts ? localUrl(`/data/segments/${segment}/people`) : undefined}
            tooltip={getViewContactsTooltip({ hasGroup: Boolean(segment), hasErrors, state }, t)}
            tooltipPosition={'left'}
            type={'link'}
          >
            {t('View Contacts')}
          </ActionMenuCell.Item>
        ),
        <ActionMenuCell.Item
          disabled={isDraft}
          href={original.downloadUrl}
          key={'download-file'}
          type={'link'}
        >
          {t('Download File')}
        </ActionMenuCell.Item>,
        hasErrors && (
          <ActionMenuCell.Item
            href={original.downloadErrorUrl}
            key={'download-errors'}
            type={'link'}
          >
            {t('Download Errors')}
          </ActionMenuCell.Item>
        ),
        // A draft is deleted; a complete or failed import is archived (`modalType`).
        (isDraft || archivable) && (
          <ActionMenuCell.Item
            key={modalType}
            onClick={openModal}
            prefix={getIconForAction(modalType)}
            theme={'danger'}
          >
            {modalType === 'delete' ? t('Delete') : t('Archive')}
          </ActionMenuCell.Item>
        ),
      ];

  async function handleConfirm(): Promise<void> {
    const action =
      modalType === 'delete'
        ? original.deleteImport.bind(original, original.id)
        : original.archive.bind(original);
    await handleAction(action);
  }

  return (
    <>
      <ActionMenuCell items={items} name={'import-options'} />
      <ArchiveModal
        {...modalProps}
        cancelButtonText={t('Cancel')}
        confirmButtonType={'danger'}
        onClose={closeModal}
        onConfirm={handleConfirm}
        opened={showArchiveModal}
        t={t}
      />
    </>
  );
});

/*
 * The standard row-actions column's id, pinning, alignment, width, and disabled sorting.
 * `optionsColumn` replaces its cell, so the items callback never runs.
 */
const ACTION_MENU_COLUMN_SHAPE = createActionMenuColumn<Importer>(() => null);

/**
 * The standard row-actions column shape, with the cell swapped for `OptionsCell`. The factory's own
 * cell renders only the menu, and its items unmount when the menu closes; `OptionsCell` keeps the
 * archive/delete modal beside the menu so the modal stays open after the item that opened it is
 * gone.
 */
export default function optionsColumn(showRunDetails = false): ColumnDef<Importer> {
  return {
    ...ACTION_MENU_COLUMN_SHAPE,
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <OptionsCell original={row.original} showRunDetails={showRunDetails} />
    ),
  };
}
